import { useAtomValue } from "@effect/atom-react";
import { useEffect } from "react";
import { useLocation, useNavigate } from "@tanstack/react-router";

import { clearCloudAuthFailure, cloudAuthFailureAtom } from "../rpc/wsConnectionState";
import { readRuntimeSurfaceState } from "../environments/runtimeSurface";
import { resolveWebCloudAuthRedirectAction } from "./WebCloudAuthRedirectCoordinator.logic";

/**
 * WEB-mode CMMD auth expiry redirect coordinator.
 *
 * Mounted ABOVE the authenticated/unauthenticated branch split in __root.tsx so
 * it runs regardless of whether we are on /login or not. This is essential for
 * the loop-break: when the redirect fires and lands on /login, this component
 * is still mounted and clears the atom so the next successful login doesn't
 * immediately re-fire the redirect.
 *
 * Sequence: /foo (expired) → navigate /login → atom cleared → user re-auths →
 * load-gate sees fresh CMMD identity → authenticated → no failure → no redirect.
 *
 * Desktop-cloud surfaces are never touched here — the existing
 * CloudSessionExpiredCoordinator toast handles them.
 */
export function WebCloudAuthRedirectCoordinator() {
  const authFailureState = useAtomValue(cloudAuthFailureAtom);
  const pathname = useLocation({ select: (location) => location.pathname });
  const navigate = useNavigate();

  useEffect(() => {
    // readRuntimeSurfaceState accesses window — must be in an effect, not render.
    const { surface } = readRuntimeSurfaceState();
    const action = resolveWebCloudAuthRedirectAction({
      failureState: authFailureState,
      surface,
      pathname,
    });

    if (action.kind === "navigate-login") {
      void navigate({ to: "/login", replace: true });
    } else if (action.kind === "clear-failure") {
      clearCloudAuthFailure();
    }
    // "none" → nothing to do.
  }, [authFailureState, pathname, navigate]);

  return null;
}
